import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { translations } from "@/i18n/translations";

const LanguageContext = createContext();

// URL path → dil tespiti
function detectLangFromPath(pathname) {
  if (pathname.startsWith("/ar")) return "ar";
  if (
    pathname === "/en" ||
    pathname.startsWith("/en/") ||
    pathname === "/features" ||
    pathname === "/pricing" ||
    pathname === "/faq" ||
    pathname === "/contact" ||
    pathname === "/privacy-policy" ||
    pathname === "/terms-of-service"
  ) {
    return "en";
  }
  return "tr";
}

// Aynı sayfanın hedef dildeki path'ini üret
const pageRoutes = {
  tr: {
    home: "/",
    features: "/ozellikler",
    pricing: "/fiyatlandirma",
    faq: "/sss",
    contact: "/iletisim",
    privacy: "/gizlilik-politikasi",
    terms: "/kullanim-kosullari",
  },
  en: {
    home: "/en",
    features: "/features",
    pricing: "/pricing",
    faq: "/faq",
    contact: "/contact",
    privacy: "/privacy-policy",
    terms: "/terms-of-service",
  },
  ar: {
    home: "/ar",
    features: "/ar/ozellikler",
    pricing: "/ar/fiyatlandirma",
    faq: "/ar/sss",
    contact: "/ar/iletisim",
    privacy: "/ar/gizlilik-politikasi",
    terms: "/ar/kullanim-kosullari",
  },
};

// Mevcut path'in hangi sayfa olduğunu bul
function detectPageFromPath(pathname) {
  const normalized = pathname.replace(/\/$/, "") || "/";
  for (const lang of ["tr", "en", "ar"]) {
    for (const [page, route] of Object.entries(pageRoutes[lang])) {
      if (normalized === route) return page;
    }
  }
  return "home";
}

export function LanguageProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [lang, setLangState] = useState(() => detectLangFromPath(location.pathname));

  // URL değiştiğinde dili senkronize et
  useEffect(() => {
    const detected = detectLangFromPath(location.pathname);
    if (detected !== lang) setLangState(detected);
  }, [location.pathname, lang]);

  // <html lang> ve <html dir> attribute'larını aktif dile göre senkronize et.
  // Prerender açılışında AR sayfası zaten <html lang="ar" dir="rtl"> ile geliyor,
  // ama client-side dil değişiminde (TR→AR dropdown) <html> dokunulmazsa
  // [lang="ar"] CSS kuralları eşleşmiyor → başlıklar Cairo fontunu almıyor.
  useEffect(() => {
    const isRTL = lang === "ar";
    document.documentElement.lang = lang;
    document.documentElement.dir = isRTL ? "rtl" : "ltr";
  }, [lang]);

  // Dil değiştirici — kullanıcı dil seçtiğinde aynı sayfanın hedef dildeki URL'sine yönlendir
  const setLang = useCallback(
    (newLang) => {
      if (!["tr", "en", "ar"].includes(newLang)) return;
      const currentPage = detectPageFromPath(location.pathname);
      const targetPath = pageRoutes[newLang]?.[currentPage] || "/";
      setLangState(newLang);
      navigate(targetPath);
    },
    [location.pathname, navigate]
  );

  const t = useCallback(
    (path) => {
      const keys = path.split(".");
      let result = translations[lang];
      for (const key of keys) {
        if (result && result[key] !== undefined) {
          result = result[key];
        } else {
          return path;
        }
      }
      return result;
    },
    [lang]
  );

  const isRTL = lang === "ar";

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, isRTL }}>
      <div dir={isRTL ? "rtl" : "ltr"}>{children}</div>
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) throw new Error("useLanguage must be used within LanguageProvider");
  return context;
}
